/**
 * Analytics Service - 100% Production Ready
 * Handles all analytics and reporting for super admin
 * Backend: /admin/analytics
 */

import apiService from './apiClient';
import logger from '../utils/logger';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface AnalyticsOverview {
  clientStats: {
    total: number;
    active: number;
    trial: number;
    suspended: number;
    inactive: number;
  };
  revenueMetrics: {
    mrr: number;
    arr: number;
    growth: number;
  };
  trialConversion: {
    totalTrials: number;
    converted: number;
    conversionRate: number;
  };
  subscriptionDistribution: Record<string, number>;
  systemHealth: {
    uptime: number;
    status: string;
  };
}

export interface ClientStatistics {
  totalClients: number;
  activeClients: number;
  trialClients: number;
  suspendedClients: number;
  inactiveClients: number;
  newClientsThisMonth: number;
  churnedThisMonth: number;
  churnRate: number;
}

export interface RevenueAnalytics {
  mrr: number;
  arr: number;
  growthRate: number;
  revenueByPlan: Record<string, number>;
  topRevenueClients: Array<{
    organizationId: number;
    name: string;
    revenue: number;
  }>;
}

export interface TrialAnalytics {
  activeTrials: number;
  expiring7Days: number;
  expiring30Days: number;
  conversionRate: number;
  averageTrialDuration: number;
}

export interface SubscriptionAnalytics {
  total: number;
  active: number;
  trial: number;
  expired: number;
  suspended: number;
  cancelled: number;
  distributionByPlan: Record<string, number>;
  churnRate: number;
  expiringIn30Days: number;
}

export interface RevenueTrend {
  month: string;
  mrr: number;
  arr: number;
  newSubscriptions: number;
  churn: number;
}

export interface SystemHealth {
  uptime: number;
  database: {
    status: string;
    connectionCount: number;
  };
  memory: {
    used: number;
    total: number;
    percentage: number;
  };
}

// ============================================================================
// ANALYTICS SERVICE
// ============================================================================

class AnalyticsService {
  private readonly BASE_PATH = '/admin/analytics';

  /**
   * Get comprehensive analytics overview
   */
  async getOverview(): Promise<any> {
    try {
      logger.info('Fetching analytics overview');

      const response = await apiService.get(`${this.BASE_PATH}/comprehensive`);

      logger.info('Analytics overview fetched');

      return response;
    } catch (error: any) {
      logger.error('Failed to fetch analytics overview', error);
      throw error;
    }
  }

  /**
   * Get client statistics
   */
  async getClientStatistics(): Promise<any> {
    try {
      logger.info('Fetching client statistics');

      const response = await apiService.get(`${this.BASE_PATH}/clients/statistics`);

      logger.info('Client statistics fetched');

      return response;
    } catch (error: any) {
      logger.error('Failed to fetch client statistics', error);
      throw error;
    }
  }

  /**
   * Get revenue analytics
   */
  async getRevenueAnalytics(): Promise<any> {
    try {
      logger.info('Fetching revenue analytics');

      const response = await apiService.get(`${this.BASE_PATH}/revenue/details`);

      logger.info('Revenue analytics fetched');

      return response;
    } catch (error: any) {
      logger.error('Failed to fetch revenue analytics', error);
      throw error;
    }
  }

  /**
   * Get trial analytics
   */
  async getTrialAnalytics(): Promise<any> {
    try {
      logger.info('Fetching trial analytics');

      const response = await apiService.get(`${this.BASE_PATH}/trials/details`);

      logger.info('Trial analytics fetched');

      return response;
    } catch (error: any) {
      logger.error('Failed to fetch trial analytics', error);
      throw error;
    }
  }

  /**
   * Get subscription analytics
   */
  async getSubscriptionAnalytics(): Promise<any> {
    try {
      logger.info('Fetching subscription analytics');

      const response = await apiService.get(`${this.BASE_PATH}/subscriptions/details`);

      logger.info('Subscription analytics fetched');

      return response;
    } catch (error: any) {
      logger.error('Failed to fetch subscription analytics', error);
      throw error;
    }
  }

  /**
   * Get revenue trend over time
   */
  async getRevenueTrend(months: number = 12): Promise<any> {
    try {
      logger.info('Fetching revenue trend', { months });

      const response = await apiService.get(`${this.BASE_PATH}/revenue/trend`, {
        params: { months },
      });

      logger.info('Revenue trend fetched');

      return response;
    } catch (error: any) {
      logger.error('Failed to fetch revenue trend', error);
      throw error;
    }
  }

  /**
   * Get system health metrics
   */
  async getSystemHealth(): Promise<any> {
    try {
      logger.info('Fetching system health');

      const response = await apiService.get(`${this.BASE_PATH}/system/health`);

      logger.info('System health fetched');

      return response;
    } catch (error: any) {
      logger.error('Failed to fetch system health', error);
      throw error;
    }
  }

  /**
   * Get subscription metrics
   */
  async getSubscriptionMetrics(): Promise<any> {
    try {
      logger.info('Fetching subscription metrics');

      const response = await apiService.get(`${this.BASE_PATH}/subscriptions`);

      logger.info('Subscription metrics fetched');

      return response;
    } catch (error: any) {
      logger.error('Failed to fetch subscription metrics', error);
      throw error;
    }
  }

  /**
   * Get trial metrics
   */
  async getTrialMetrics(): Promise<any> {
    try {
      logger.info('Fetching trial metrics');

      const response = await apiService.get(`${this.BASE_PATH}/trials`);

      logger.info('Trial metrics fetched');

      return response;
    } catch (error: any) {
      logger.error('Failed to fetch trial metrics', error);
      throw error;
    }
  }

  /**
   * Get client metrics
   */
  async getClientMetrics(): Promise<any> {
    try {
      logger.info('Fetching client metrics');

      const response = await apiService.get(`${this.BASE_PATH}/clients`);

      logger.info('Client metrics fetched');

      return response;
    } catch (error: any) {
      logger.error('Failed to fetch client metrics', error);
      throw error;
    }
  }

  /**
   * Get revenue metrics
   */
  async getRevenueMetrics(): Promise<any> {
    try {
      logger.info('Fetching revenue metrics');

      const response = await apiService.get(`${this.BASE_PATH}/revenue`);

      logger.info('Revenue metrics fetched');

      return response;
    } catch (error: any) {
      logger.error('Failed to fetch revenue metrics', error);
      throw error;
    }
  }
}

// Create and export singleton instance
const analyticsService = new AnalyticsService();
export default analyticsService;
